<template>
	<v-container fluid>
		<v-row class="mb-4" align="center">
			<v-col>
				<h2 class="text-primary text-h5 font-weight-medium">
					Modèles des produits ({{ filteredModels.length }}/{{ totalModels }})
				</h2>
			</v-col>

			<v-col cols="auto">
				<v-btn variant="text" class="mr-2" prepend-icon="mdi-arrow-left" @click="goToMetadata">
					Retour Metadata
				</v-btn>
			</v-col>

			<v-col cols="auto">
				<v-btn color="primary" prepend-icon="mdi-plus" @click="createModel">
					Nouveau Modèle
				</v-btn>
			</v-col>
		</v-row>

		<v-card rounded="xl" elevation="2" class="mb-4">
			<v-card-text>
				<v-row dense>
					<v-col cols="12" md="4">
						<v-autocomplete
							v-model="selectedMake"
							:items="productMakes"
							label="Filtrer par marque"
							clearable
							density="compact"
						/>
					</v-col>

					<v-col cols="12" md="4">
						<v-text-field
							v-model="selectedModel"
							label="Filtrer par modèle"
							clearable
							density="compact"
						/>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>

		<v-card rounded="xl" elevation="2">
			<v-data-table :items="filteredModels" :loading="loading" item-key="id" hover class="product-model-table">
				<template #headers>
					<tr>
						<th>Marque</th>
						<th>Modèle</th>
						<th></th>
					</tr>
				</template>

				<template #item="{ item }">
					<tr class="table-row" @click="goToEditor(item)">
						<td>
							<div class="font-weight-medium">
								{{ item.productMake }}
							</div>
						</td>

						<td>
							<div class="font-weight-medium">
								{{ item.model }}
							</div>
						</td>

						<td class="text-right">
							<v-btn icon="mdi-delete" color="error" variant="text" density="comfortable" @click.stop="askDeleteModel(item)" />
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card>

		<ConfirmDialog
			v-model="deleteDialog"
			dialog-title="Suppression"
			:dialog-text="deleteDialogText"
			@confirm="confirmDeleteModel"
		/>
	</v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { useAppStore } from '@/store/app'
import { deleteProductModel, fetchProductModels } from '@/utils/product-model-api'
import { ProductMake } from '@/types'
import type { ProductModel } from '@/types/product-model'

const router = useRouter()
const appStore = useAppStore()

const models = ref<ProductModel[]>([])
const loading = ref(false)
const selectedMake = ref<ProductMake | null>(null)
const selectedModel = ref('')
const deleteDialog = ref(false)
const modelToDelete = ref<ProductModel | null>(null)
const deleteDialogText = computed(() =>
	modelToDelete.value ? `Voulez-vous supprimer le modèle "${modelToDelete.value.model}" ?` : ''
)

const totalModels = computed(() => models.value.length)
const productMakes = Object.values(ProductMake)
const filteredModels = computed(() => {
	if (!selectedMake.value) {
		return models.value.filter((model) => {
			const modelFilter = selectedModel.value.trim().toLowerCase()
			return !modelFilter || model.model.toLowerCase().includes(modelFilter)
		})
	}

	return models.value.filter((model) => {
		const modelFilter = selectedModel.value.trim().toLowerCase()
		const matchesMake = model.productMake === selectedMake.value
		const matchesModel = !modelFilter || model.model.toLowerCase().includes(modelFilter)
		return matchesMake && matchesModel
	})
})

async function fetchModels() {
	loading.value = true
	try {
		models.value = await fetchProductModels()
	} finally {
		loading.value = false
	}
}

function goToEditor(model: ProductModel) {
	router.push({
		name: 'ProductModelEditor',
		params: { id: model.id }
	})
}

function createModel() {
	router.push({
		name: 'ProductModelEditor',
		params: { id: 'new' }
	})
}

function goToMetadata() {
	router.push({ name: 'metadata' })
}

function askDeleteModel(model: ProductModel) {
	modelToDelete.value = model
	deleteDialog.value = true
}

async function confirmDeleteModel() {
	if (!modelToDelete.value) {
		return
	}

	try {
		await deleteProductModel(modelToDelete.value.id)
		models.value = models.value.filter((model) => model.id !== modelToDelete.value?.id)
		appStore.sendSuccessNotice('Modèle supprimé avec succès')
	} catch (error) {
		appStore.sendErrorNotice('Échec de la suppression du modèle')
	} finally {
		modelToDelete.value = null
	}
}

onMounted(fetchModels)
</script>

<style scoped>
.table-row {
	cursor: pointer;
	transition: background 0.15s ease;
}

.table-row:hover {
	background: rgba(var(--v-theme-primary), 0.05);
}
</style>
